/**
 * The phone saying, out loud, that something is wrong.
 *
 * Until 15 August every alarm on this platform watched from inside the server. That
 * fortnight proved why it is the wrong end to watch from: the server answered the guide
 * in four hundred milliseconds the entire time, while every browser in the world failed
 * to receive it, because the failure was on the phone. The server saw nothing because
 * there was nothing to see.
 *
 * So this is the instrument. It is also the fastest one there is — a timer can be a
 * minute stale, and a device says so the instant it fails.
 *
 * Three rules hold it in check. It reports a given kind once per page load, so a screen
 * retrying in a loop cannot become a hundred alerts. It goes out by beacon, so it
 * survives somebody closing the tab a second later. And it can never throw, because a
 * fault report turning into a fault would be a poor joke.
 */

const REPORT_URL = "/api/public/client-fault";

export type FaultKind =
  /** The schedule came out of the cupboard rather than off the network. */
  | "guide_fallback"
  /** No schedule at all. The reader is looking at an error where a convention should be. */
  | "guide_missing"
  /** Something threw where nobody caught it. */
  | "script_error"
  /** A request to this platform failed. */
  | "api_failure"
  /** The offline machinery would not install, so this device has no safety net. */
  | "service_worker_failed";

/** One of each per page load. The second identical report tells nobody anything new. */
const alreadySaid = new Set<FaultKind>();

/**
 * Which build is complaining. A fault only one build reports is a fault that shipped,
 * which is the most useful single thing to know at three in the morning. Read off the
 * script tag rather than baked in, so it is right without a build step knowing about it.
 */
function currentBuild(): string | null {
  try {
    const script = document.querySelector<HTMLScriptElement>('script[src*="/assets/"]');
    return script?.src.match(/\/assets\/([^/]+?)\.js/u)?.[1] ?? null;
  } catch {
    return null;
  }
}

export function reportFault(kind: FaultKind, detail?: string | null): void {
  if (alreadySaid.has(kind)) return;
  alreadySaid.add(kind);
  const body = JSON.stringify({ kind, build: currentBuild(), detail: detail ?? null });
  try {
    if (navigator.sendBeacon?.(REPORT_URL, new Blob([body], { type: "application/json" }))) return;
    void fetch(REPORT_URL, {
      method: "POST", body, keepalive: true, headers: { "content-type": "application/json" },
    }).catch(() => undefined);
  } catch {
    // Nothing here is worth interrupting a reader for.
  }
}

/** For tests, which are each their own page load. */
export function forgetReportedFaults(): void {
  alreadySaid.clear();
}

/**
 * Listens for the failures nobody wrote a handler for.
 *
 * A script error usually means a bad build, and a bad build usually means every phone at
 * once — which is precisely the signal worth having and precisely the one nothing was
 * collecting. Registered once, at startup, and deliberately passive: it never prevents
 * the browser's own reporting and never swallows anything.
 */
export function watchForFaults(target: Window = window): void {
  target.addEventListener("error", (event) => {
    // Ignore the noise from an image or stylesheet that would not load; those are not
    // the application breaking, and they arrive constantly on bad wifi.
    if (event.target !== target) return;
    reportFault("script_error", event.message);
  });
  target.addEventListener("unhandledrejection", (event) => {
    const reason: unknown = (event as PromiseRejectionEvent).reason;
    reportFault("script_error", reason instanceof Error ? reason.message : String(reason));
  });
}
